/**
 * Storefront catalog URL state and the brand's default filters / sort
 * (PD-15272).
 *
 * The storefront catalog keeps every filter and sort in URL query params, so
 * a brand's defaults are applied by writing them INTO the URL when a shopper
 * opens the catalog, rather than being merged in on every request. That keeps
 * one source of truth (the URL): the chips, the rail, the sort dropdown and
 * the request all read the same params, and once the defaults are in the URL
 * the shopper changes them exactly like filters they picked themselves.
 *
 * The defaults are applied once per visit. Applying them adds a marker param
 * that every later interaction carries along, so turning a default off, or
 * "Clear all", really does leave the catalog unfiltered instead of the
 * defaults reappearing on the next render. Opening the catalog afresh (a nav
 * link, a dashboard tile) has no marker, so the defaults apply again.
 */

/** Prefix of the facet-rail params (`f.manufacturer`, `f.price.min`, …). */
export const STOREFRONT_FACET_PARAM_PREFIX = 'f.';

/**
 * Boolean params that predate the facet rail: the dynamic toggles keep their
 * own names (`?isInStock=true`) and the per-customer lenses (favorites,
 * previously purchased) are reserved keys outside the rail.
 */
export const STOREFRONT_LEGACY_TOGGLE_PARAMS = [
	'isInStock',
	'isShortDate',
	'isFavorite',
	'isPreviouslyPurchased',
	'isOnOrder',
	'isOnPromo',
] as const;

/**
 * Marker written when the defaults have been applied for this visit. Its
 * presence means "the shopper owns the filters now" and stops the defaults
 * being applied again.
 */
export const STOREFRONT_CATALOG_DEFAULTS_PARAM = 'defaults';
export const STOREFRONT_CATALOG_DEFAULTS_APPLIED = 'applied';

export interface StorefrontCatalogDefaultsPlanModel {
	/** URL params to switch on (`'true'`), one per boolean filter on by default. */
	filterParams: ReadonlyArray<string>;
	/** The default sort's URL params, or null to keep the standard order. */
	sort: { sortBy: string; sortOrder: string | null } | null;
}

/**
 * Whether the URL already carries a filter or sort the shopper (or the link
 * that brought them here) chose. Category, subcategory, search text, promo
 * scope and page are navigation, not a filter choice, so they do not count —
 * a shopper arriving from a category tile still gets the brand's defaults.
 */
export function hasExplicitStorefrontFilterOrSort(
	searchParams: URLSearchParams,
): boolean {
	for (const key of searchParams.keys()) {
		if (key.startsWith(STOREFRONT_FACET_PARAM_PREFIX)) {
			return true;
		}
	}

	if (
		STOREFRONT_LEGACY_TOGGLE_PARAMS.some((param) => searchParams.has(param))
	) {
		return true;
	}

	return searchParams.has('sortBy') || searchParams.has('sortOrder');
}

/**
 * The URL params to replace the current ones with so the brand's defaults
 * apply, or null when nothing should change:
 *  - the brand has no defaults (every storefront without the setting keeps
 *    its URLs exactly as before),
 *  - the defaults were already applied on this visit (marker present), which
 *    is what makes "Clear all" and turning a default off stick.
 *
 * When the URL already carries an explicit filter or sort (a deep link), only
 * the marker is added: the link's own choice wins and the defaults stay out,
 * and the marker keeps them out after the shopper later clears that choice.
 */
export function applyStorefrontCatalogDefaults(
	searchParams: URLSearchParams,
	plan: StorefrontCatalogDefaultsPlanModel,
): URLSearchParams | null {
	if (plan.filterParams.length === 0 && plan.sort == null) {
		return null;
	}

	if (searchParams.has(STOREFRONT_CATALOG_DEFAULTS_PARAM)) {
		return null;
	}

	const next = new URLSearchParams(searchParams);

	if (!hasExplicitStorefrontFilterOrSort(searchParams)) {
		// A changed filter set makes a carried-over page number meaningless,
		// the same rule every filter interaction on the catalog follows.
		next.delete('page');

		for (const param of plan.filterParams) {
			next.set(param, 'true');
		}

		if (plan.sort != null) {
			next.set('sortBy', plan.sort.sortBy);

			if (plan.sort.sortOrder != null) {
				next.set('sortOrder', plan.sort.sortOrder);
			}
		}
	}

	next.set(
		STOREFRONT_CATALOG_DEFAULTS_PARAM,
		STOREFRONT_CATALOG_DEFAULTS_APPLIED,
	);

	return next;
}
